import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import './App.css';
import querystring from 'querystring';
import { API_BASE_URL, STRAVA_CLIENT_ID } from './config';
import ActivityList from './components/ActivityList';
import Stats from './components/Stats';
import Training from './components/Training';
import NotFound from './pages/NotFound';
import { usePageMeta } from './utils/pageMeta';
import MobileDetect from 'mobile-detect';
import Footer from './components/Footer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Header from './components/Header';
import TermsOfService from './pages/TermsOfService';
import FAQ from './pages/FAQ';

const getStraveAuthorizeUrl = (clientId: string, redirectURI: string) => {
    const md = new MobileDetect(window.navigator.userAgent);
    var url = md.mobile()
        ? 'https://www.strava.com/oauth/mobile/authorize?'
        : 'https://www.strava.com/oauth/authorize?';
    var oauthArgs = {
        client_id: clientId,
        redirect_uri: redirectURI,
        response_type: 'code',
        scope: 'activity:write,activity:read,activity:read_all',
    };

    var qs = querystring.stringify(oauthArgs);

    url += qs;
    return url;
};

const handleAuthorizeClick = () => {
    const clientId = STRAVA_CLIENT_ID;
    // Use either localhost or your production URL (https://streventools.com/strava-callback)   
    // Make sure to set the redirect URI (authorization callback domain) in your Strava app settings to match this URL
    const redirectURI = `${window.location.origin}/strava-callback`;
    const url = getStraveAuthorizeUrl(clientId, redirectURI);
    window.location.href = url;
};

const Home: React.FC = () => {
    usePageMeta({
        title: 'Streven: Strava Activity Tools & Training Plans',
        description: 'Free Strava tools: combine activities, round distance up or down, see your stats, and build a coached training plan for your next 5K, 10K, half or marathon.',
        path: '/',
    });

    const [athlete, setAthlete] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab: 'activities' | 'stats' | 'training' =
        searchParams.get('tab') === 'stats' ? 'stats'
        : searchParams.get('tab') === 'training' ? 'training'
        : 'activities';
    const setActiveTab = (tab: 'activities' | 'stats' | 'training') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tab === 'activities') next.delete('tab');
            else next.set('tab', tab);
            return next;
        });
    };

    useEffect(() => {
        const checkToken = async () => {
            const token = localStorage.getItem('token');
            const athleteInfo = localStorage.getItem('athlete');

            if (token) {
                setIsLoading(true);
                const parsedToken = JSON.parse(token);
                const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
                if (parsedToken.expiresAt <= currentTime) {
                    console.debug("Refreshing access token...");
                    await refreshAccessToken(parsedToken.refreshToken);
                }
                if (athleteInfo) {
                    setAthlete(JSON.parse(athleteInfo));
                }
                setIsLoading(false);
            }
        };

        checkToken();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('athlete');
        setAthlete(null);
        window.location.reload(); // Reload the page to reset the state
    };

    return (
        <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '100vw', justifyContent: 'space-between' }}>
            <Header />
            <main style={{ paddingTop: '5px', paddingBottom: '5px', textAlign: 'center' }}>
                {/* TODO - Cleanup to avoid nested ternary operators */}
                {isLoading ? (<StravaLoading message="Loading your profile..." />)
                    : (athlete ? (
                        <div>
                            <button
                                onClick={handleLogout}
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    backgroundColor: 'white',
                                    color: '#FC4C02',
                                    border: 'none',
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                Log Out
                            </button>
                            <div className="activities-header-row" style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 0 }}>
                                <div className="activities-header-spacer" style={{ flex: 1 }} /> {/* Center tabs */}
                                <div className="activities-header-tabs" style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setActiveTab('activities')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            padding: '0 8px 4px',
                                            borderBottom: activeTab === 'activities' ? '3px solid #FC4C02' : '3px solid transparent',
                                            color: activeTab === 'activities' ? '#FC4C02' : '#888',
                                        }}
                                    >
                                        Activities
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('stats')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            padding: '0 8px 4px',
                                            borderBottom: activeTab === 'stats' ? '3px solid #FC4C02' : '3px solid transparent',
                                            color: activeTab === 'stats' ? '#FC4C02' : '#888',
                                        }}
                                    >
                                        Stats
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('training')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            padding: '0 8px 4px',
                                            borderBottom: activeTab === 'training' ? '3px solid #FC4C02' : '3px solid transparent',
                                            color: activeTab === 'training' ? '#FC4C02' : '#888',
                                        }}
                                    >
                                        Training
                                    </button>
                                </div>
                                <div className="activities-header-user" style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '5px' }}>
                                    <h3 style={{ margin: 0 }}>{athlete.firstname}</h3>
                                    <img src={athlete.profile.startsWith("https:") ? athlete.profile : 'blank-user-icon.png'} alt="Athlete Profile" style={{ borderRadius: '50%', width: '40px', height: '40px', paddingRight: '10px' }} />
                                </div>
                            </div>
                            {activeTab === 'activities' ? <ActivityList athlete={athlete} />
                                : activeTab === 'stats' ? <Stats athlete={athlete} />
                                : <Training athlete={athlete} />}
                        </div>
                    ) : (
                        <section style={{ marginTop: '30px' }}>
                            <h1 style={{ margin: '0 auto 8px', maxWidth: '640px', padding: '0 16px', fontSize: '1.8rem' }}>Free Strava tools for runners and cyclists</h1>
                            <p style={{ maxWidth: '560px', margin: '0 auto 16px', padding: '0 16px', color: '#555', lineHeight: 1.6 }}>
                                Fix activities that stopped too soon or came up a few metres short, see how your training is trending, and build a week-by-week plan for your next race that you can change just by chatting.
                            </p>
                            <button
                                style={{
                                    padding: '10px 20px',
                                    cursor: 'pointer',
                                    border: 'none',
                                    backgroundColor: 'transparent'
                                }}
                                onClick={handleAuthorizeClick}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.opacity = '0.8';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.opacity = '1';
                                }}
                            >
                                <img
                                    src="/connect-with-strava.svg"
                                    alt="Connect with Strava"
                                    style={{ width: '200px', height: 'auto' }}
                                />
                            </button>
                            <section style={{ marginTop: '30px', marginBottom: '30px', textAlign: 'left', maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '24px' }}>
                                <h2 style={{ color: '#FC4C02', marginTop: 0, marginBottom: '16px', fontSize: '1.4rem' }}>Features</h2>
                                <ul style={{ fontSize: '1.0rem', lineHeight: '1.7', paddingLeft: '20px', margin: 0 }}>
                                    <li style={{ marginBottom: '12px' }}>
                                        <h3 style={{ fontSize: '1.05rem', margin: 0 }}>Training plans for your next race</h3>
                                        Pick a 5K, 10K, half marathon or marathon, a race date and a target time. Streven builds a week-by-week plan from your Strava history, with a realism score and a difficulty score so you know what you're signing up for. Keep up to five plans, and your sessions tick off automatically as matching Strava activities come in.
                                    </li>
                                    <li style={{ marginBottom: '12px' }}>
                                        <h3 style={{ fontSize: '1.05rem', margin: 0 }}>Change your plan by chatting</h3>
                                        Tell the coach what you want in plain words: "I only want to run 3 times a week", "add a swim on Tuesdays", "add a weights session each week", "show the day of the week for each session" or "I'm on holiday in November, only 1-2 runs". It also moves your race date or target time (after you confirm) and remembers your preferences when the plan is rebuilt.
                                    </li>
                                    <li style={{ marginBottom: '12px' }}>
                                        <h3 style={{ fontSize: '1.05rem', margin: 0 }}>Training stats</h3>
                                        See your weekly distance and pace trends, streaks, a calendar of your activity, gear mileage, race time predictions and a short written summary of your training.
                                    </li>
                                    <li style={{ marginBottom: '12px' }}>
                                        <h3 style={{ fontSize: '1.05rem', margin: 0 }}>Combine Strava Activities</h3>
                                        Accidentally stopped your activity too soon, or had a long break before continuing? This tool allows you to select two different Strava activities and will combine them for you, so your stats and records stay accurate.
                                    </li>
                                    <li style={{ marginBottom: '12px' }}>
                                        <h3 style={{ fontSize: '1.05rem', margin: 0 }}>Round Up Activity Distance</h3>
                                        Ran a 10km race and Strava shows 9.98km? This tool allows you to recreate an activity while "rounding up" the distance, so your stats and records stay accurate. For example, if you ran 9.98km, it will create a new activity with exactly 10.0km.
                                    </li>
                                    <li>
                                        <h3 style={{ fontSize: '1.05rem', margin: 0 }}>Round Down Activity Distance</h3>
                                        Accidentally stopped your activity too late, or Strava added a 0.1km Strava tax? This tool allows you to recreate an activity while "rounding down" the distance, so your stats and records stay accurate. For example, if you ran 10.3km, it will create a new activity with exactly 10.0km.
                                    </li>
                                </ul>
                                <p style={{ fontSize: '0.9rem', color: '#888', marginBottom: 0 }}>
                                    Training plans are written by an AI model and are suggestions, not medical or professional coaching advice. <a href="/faq" style={{ color: '#FC4C02' }}>Read the FAQ</a> for details.
                                </p>
                            </section>
                        </section>
                    ))}
            </main>
            <Footer />
        </div>
    );
};

const fetchAccessToken = async (code: string) => {
    try {
        const response = await fetch(`${API_BASE_URL}/access-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ authorizationCode: code }),
        });

        const data = await response.json();

        if (data.accessToken) {
            const tokenInfo = {
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: data.expiresAt,
                tokenType: data.tokenType,
                expiresIn: data.expiresIn,
            }
            localStorage.setItem('token', JSON.stringify(tokenInfo)); // Store temporary access token in local storage
            console.log('Access token stored in local storage.');
        } else {
            console.error('Access token not found in response.');
        }
        if (data.athlete) {
            console.log('Athlete info:', data.athlete);
            localStorage.setItem('athlete', JSON.stringify(data.athlete)); // Store athlete info in local storage
            console.log('Athlete info stored in local storage.');
        }

        // redirect to home page
        window.location.href = '/';
    } catch (error) {
        console.error('Error fetching access token:', error);
    }
};

const refreshAccessToken = async (refreshToken: string) => {
    try {
        const response = await fetch(`${API_BASE_URL}/refresh-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
        });

        const data = await response.json();

        if (data.accessToken) {
            const tokenInfo = {
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: data.expiresAt,
                tokenType: data.tokenType,
                expiresIn: data.expiresIn,
            }
            localStorage.setItem('token', JSON.stringify(tokenInfo)); // Store temporary access token in local storage
            console.log('Access token refreshed and stored in local storage.');
        } else {
            console.error('Access token not found in refresh token response.');
        }
    } catch (error) {
        console.error('Error refreshing access token:', error);
    }
};

const StravaLoading: React.FC<{ message?: string }> = ({ message = 'Authorizing with Strava...' }) => (
    <>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="spinner" style={{
                width: '50px',
                height: '50px',
                border: '5px solid rgba(0, 0, 0, 0.1)',
                borderTop: '5px solid #FC4C02',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
            }}></div>
            <p style={{ marginTop: '20px', fontSize: '1.2rem', color: '#333' }}>{message}</p>
        </div>
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </>
);

const StravaCallback: React.FC = () => {
    usePageMeta({
        title: 'Connecting to Strava | Streven',
        description: 'Connecting your Strava account to Streven.',
        path: '/strava-callback',
        noindex: true,
    });

    const location = useLocation();
    const [error, setError] = useState<string | null>(null);

    React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get('code');
        const errorParam = queryParams.get('error');

        if (errorParam) {
            setError('Failed to authenticate with Strava. Please try again.');
            return;
        }

        if (code) {
            console.log('Authorization code:', code);
            const response = fetchAccessToken(code);
            console.log('Response:', response);
        } else {
            console.error('Authorization code not found in query parameters.');
        }
    }, [location]);

    const handleCloseError = () => {
        setError(null);
        window.location.href = '/';
    };

    return (
        <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            {error && (
                <div className="modal" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        textAlign: 'center',
                    }}>
                        <p>{error}</p>
                        <button onClick={handleCloseError} style={{
                            backgroundColor: '#FC4C02',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            borderRadius: '5px',
                            marginTop: '10px',
                        }}>Close</button>
                    </div>
                </div>
            )}
            <StravaLoading />
        </div>
    );
};

const App: React.FC = () => {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/strava-callback" element={<StravaCallback />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Router>
    );
};

export default App;